/**
 * AdSense policy gate. Google requires every page that serves ads to have
 * substantial publisher content. This helper centralises the rules so we never
 * inject `<ins class="adsbygoogle">` onto pages that would violate the policy:
 *
 *   "Google-served ads on screens without publisher-content"
 *
 * Usage:
 *   const { allowed, reason } = shouldShowAds(pathname, { wordCount, ... });
 *   if (!allowed) return null;
 */

export interface ContentStats {
  /** Words of visible, human-readable publisher copy on the page. */
  wordCount?: number;
  /** Page is still loading its primary content. */
  isLoading?: boolean;
  /** Page is in an error / 404 state. */
  hasError?: boolean;
  /** Page is primarily a form for user input (composer, submit, edit). */
  isFormOnly?: boolean;
  /** Page is just a map / search UI without surrounding editorial copy. */
  isMapOrSearchOnly?: boolean;
  /** Page is empty search results. */
  isEmptyResults?: boolean;
  /** BCP-47 language tag of the visible content. */
  language?: string;
}

export interface ShouldShowAdsResult {
  allowed: boolean;
  reason: string;
}

// Routes (prefix match) where AdSense must never load. Auth, account, admin,
// payment, AI chat, and any composer/form page falls here.
const DENY_PREFIXES = [
  '/auth',
  '/login',
  '/signup',
  '/signin',
  '/sign-in',
  '/sign-up',
  '/logout',
  '/reset-password',
  '/verify',
  '/onboarding',
  '/dashboard',
  '/settings',
  '/account',
  '/profile',
  '/admin',
  '/chat',
  '/ai',
  '/ask',
  '/assistant',
  '/billing',
  '/checkout',
  '/payment',
  '/payments',
  '/credits',
  '/wallet',
  '/pricing',
  '/upgrade',
  '/subscribe',
  '/report',
  '/edit',
  '/new',
  '/submit',
  '/add',
  '/compose',
  '/places/add',
  '/places/edit',
  '/talk/new',
  '/talk/compose',
  '/deals/new',
  '/value/submit',
  '/404',
  '/error',
  '/not-found',
];

// AdSense's supported publisher content languages (subset relevant to us).
// See: https://support.google.com/adsense/answer/9727
const SUPPORTED_LANGUAGES = new Set([
  'mn', 'mn-MN', // Mongolian — supported since 2024
  'en', 'en-US', 'en-GB',
  'ru', 'ja', 'ko', 'zh', 'zh-CN', 'zh-TW',
  'de', 'fr', 'es', 'it', 'pt', 'tr', 'nl', 'pl', 'sv', 'da', 'no', 'fi',
]);

// AdSense's published minimum: ~400 words of original copy. We use 600 to give
// ourselves a comfortable buffer.
const MIN_WORDS = 600;

function isProd(): boolean {
  try {
    return !import.meta.env.DEV;
  } catch {
    return true;
  }
}

export function shouldShowAds(
  route: string | undefined | null,
  stats: ContentStats = {},
): ShouldShowAdsResult {
  const path = (route ?? '/').toLowerCase().split('?')[0].split('#')[0];

  // 1. Route-based denylist — never serve ads here, regardless of content.
  for (const prefix of DENY_PREFIXES) {
    if (path === prefix || path.startsWith(prefix + '/')) {
      return { allowed: false, reason: `route_denied:${prefix}` };
    }
  }

  // 2. Loading / error / empty states have no publisher content yet.
  if (stats.isLoading) return { allowed: false, reason: 'page_loading' };
  if (stats.hasError) return { allowed: false, reason: 'page_error' };
  if (stats.isEmptyResults) return { allowed: false, reason: 'empty_results' };

  // 3. Form-only / map-only screens are not publisher content.
  if (stats.isFormOnly) return { allowed: false, reason: 'form_only' };
  if (stats.isMapOrSearchOnly) return { allowed: false, reason: 'map_or_search_only' };

  // 4. Language must be one AdSense supports.
  if (stats.language) {
    const lang = stats.language.toLowerCase();
    const base = lang.split('-')[0];
    if (!SUPPORTED_LANGUAGES.has(lang) && !SUPPORTED_LANGUAGES.has(base)) {
      return { allowed: false, reason: `unsupported_language:${stats.language}` };
    }
  }

  // 5. Substantial content threshold.
  const words = stats.wordCount ?? 0;
  if (words < MIN_WORDS) {
    return { allowed: false, reason: `insufficient_content:${words}<${MIN_WORDS}` };
  }

  return { allowed: true, reason: 'ok' };
}

/**
 * Whether the policy gate should render a visible placeholder (dev only) or
 * silently render nothing (production).
 */
export function adsHiddenRenderMode(): 'placeholder' | 'null' {
  return isProd() ? 'null' : 'placeholder';
}

export const __ADS_INTERNAL__ = {
  DENY_PREFIXES,
  SUPPORTED_LANGUAGES,
  MIN_WORDS,
};